import { useState } from 'react';
import {
  CheckCircleIcon,
  WarningIcon,
  MinusIcon,
  InfoIcon,
  ArrowSquareOutIcon,
  CircleNotchIcon,
  PlusIcon,
  PaperclipIcon,
  CircleDashedIcon,
  UserCheckIcon,
  ChatIcon,
  ShieldCheckIcon,
} from '@phosphor-icons/react';
import { useNavigate } from '@tanstack/react-router';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import {
  DiscountStatus,
  type DiscountValidationResult,
  type CustomerSpecificDiscountVerification,
} from 'src/types/application/SalesOrderDraftDTO';
import MDBox from 'src/components/md/MDBox';
import MDTypography from 'src/components/md/MDTypography';
import { CreateDiscountModal } from './CreateDiscountModal';
import { AttachmentViewerModal } from '@/components/common/AttachmentViewerModal';

interface DiscountBadgeProps {
  discountValidation?: DiscountValidationResult;
  customerId?: string;
  customerName?: string;
  /** Product variant ID for creating discounts */
  productVariantId?: string;
  /** SKU for display in discount creation */
  sku?: string;
  /** Show loading spinner while discount validation is in progress */
  isLoading?: boolean;
  /** Current verification status for customer-specific discounts */
  customerSpecificDiscountVerification?: CustomerSpecificDiscountVerification;
  /** Callback when user clicks Waive button */
  onWaive?: () => void;
  /** Whether waive operation is in progress */
  isWaiving?: boolean;
}

function formatCurrency(value: number | string | null | undefined): string {
  if (value == null) {
    return '$0.00';
  }
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) {
    return '$0.00';
  }
  return `$${numValue.toFixed(2)}`;
}

function getDiscountBadgeContent(
  status: DiscountStatus,
  verificationStatus?: CustomerSpecificDiscountVerification['status'],
) {
  switch (status) {
    case DiscountStatus.STANDARD_PRICE:
      return {
        icon: <MinusIcon size={14} className="text-gray-500" />,
        label: 'Standard',
        color: 'text.secondary',
        bgcolor: 'rgba(0, 0, 0, 0.08)',
      };
    case DiscountStatus.APPROVED_DISCOUNT:
      return {
        icon: <CheckCircleIcon size={14} className="text-green-600" />,
        label: 'Approved',
        color: '#2e7d32',
        bgcolor: 'rgba(46, 125, 50, 0.1)',
      };
    case DiscountStatus.UNAPPROVED_DISCOUNT:
      return {
        icon: <WarningIcon size={14} className="text-yellow-600" />,
        label: 'Review',
        color: '#ed6c02',
        bgcolor: 'rgba(237, 108, 2, 0.1)',
      };
    case DiscountStatus.NO_LIST_PRICE:
      return {
        icon: <InfoIcon size={14} className="text-blue-500" />,
        label: 'No list price',
        color: '#0288d1',
        bgcolor: 'rgba(2, 136, 209, 0.1)',
      };
    case DiscountStatus.CUSTOMER_SPECIFIC_DISCOUNT_PENDING:
      // Show different badge based on verification status
      if (verificationStatus === 'VERIFIED') {
        return {
          icon: <UserCheckIcon size={14} className="text-green-600" />,
          label: 'Verified',
          color: '#2e7d32',
          bgcolor: 'rgba(46, 125, 50, 0.1)',
        };
      }
      if (verificationStatus === 'WAIVED') {
        return {
          icon: <ShieldCheckIcon size={14} className="text-blue-500" />,
          label: 'Waived',
          color: '#0288d1',
          bgcolor: 'rgba(2, 136, 209, 0.1)',
        };
      }
      if (verificationStatus === 'VERIFICATION_REQUESTED') {
        return {
          icon: <ChatIcon size={14} className="text-amber-500" />,
          label: 'Pending',
          color: '#f59e0b',
          bgcolor: 'rgba(245, 158, 11, 0.1)',
        };
      }
      // Default: PENDING_VERIFICATION or no verification status yet
      // Use "Review" label with purple color as differentiator
      return {
        icon: <WarningIcon size={14} className="text-purple-600" />,
        label: 'Review',
        color: '#9333ea',
        bgcolor: 'rgba(147, 51, 234, 0.1)',
      };
    default:
      return {
        icon: <MinusIcon size={14} className="text-gray-500" />,
        label: 'Unknown',
        color: 'text.secondary',
        bgcolor: 'rgba(0, 0, 0, 0.08)',
      };
  }
}

function getTooltipContent(
  validation: DiscountValidationResult,
): React.ReactNode {
  const { status, poUnitPrice, discount, discrepancy, listPrice } = validation;

  switch (status) {
    case DiscountStatus.STANDARD_PRICE:
      if (listPrice) {
        return (
          <div className="text-xs space-y-1">
            <div>
              PO: {formatCurrency(poUnitPrice)}, List Price:{' '}
              {formatCurrency(listPrice.price)}
            </div>
            <div className="text-gray-300">
              PO price meets or exceeds list price
            </div>
          </div>
        );
      }
      return (
        <span className="text-xs">
          Price matches standard price ({formatCurrency(poUnitPrice)})
        </span>
      );

    case DiscountStatus.APPROVED_DISCOUNT:
      if (discount) {
        return (
          <div className="text-xs space-y-1">
            <div>
              PO: {formatCurrency(poUnitPrice)}, Discount Price:{' '}
              {formatCurrency(discount.discountPrice)}
            </div>
            <div className="text-gray-300">
              PO price meets or exceeds discount price
            </div>
            {discount.validUntil && (
              <div className="text-gray-300">
                Valid until:{' '}
                {new Date(discount.validUntil).toLocaleDateString()}
              </div>
            )}
          </div>
        );
      }
      return <span className="text-xs">Approved discount</span>;

    case DiscountStatus.UNAPPROVED_DISCOUNT:
      // Case 1: No discount exists, but PO price is below list price
      if (!discount && listPrice && discrepancy) {
        return (
          <div className="text-xs space-y-1">
            <div className="font-medium">Unapproved price</div>
            <div>PO price is below list price with no discount applied</div>
            <div>
              PO: {formatCurrency(discrepancy.actualPrice)}, List Price:{' '}
              {formatCurrency(discrepancy.expectedPrice)}
            </div>
            <div className="text-gray-300">
              Difference: {formatCurrency(discrepancy.difference)}
            </div>
          </div>
        );
      }
      // Case 2: Discount exists, but PO price is below discount price
      if (discrepancy) {
        return (
          <div className="text-xs space-y-1">
            <div className="font-medium">PO price is below discount price</div>
            <div>
              PO: {formatCurrency(discrepancy.actualPrice)}, Discount Price:{' '}
              {formatCurrency(discrepancy.expectedPrice)}
            </div>
            <div className="text-gray-300">
              Difference: {formatCurrency(discrepancy.difference)}
            </div>
          </div>
        );
      }
      return (
        <span className="text-xs">
          PO price ({formatCurrency(poUnitPrice)}) is below discount price (
          {discount ? formatCurrency(discount.discountPrice) : 'N/A'}).
        </span>
      );

    case DiscountStatus.NO_LIST_PRICE:
      return (
        <span className="text-xs">
          No discount or list price found. Cannot validate.
        </span>
      );

    case DiscountStatus.CUSTOMER_SPECIFIC_DISCOUNT_PENDING:
      if (validation.endCustomer) {
        return (
          <div className="text-xs space-y-1">
            <div className="font-medium">End-Customer Discount</div>
            <div>
              End-customer:{' '}
              <span className="font-semibold">
                {validation.endCustomer.name}
              </span>
            </div>
            <div className="text-gray-300">Click to review or waive</div>
          </div>
        );
      }
      return (
        <span className="text-xs">
          End-customer discount requires verification
        </span>
      );

    default:
      return <span className="text-xs">Unknown discount status</span>;
  }
}

/**
 * Badge element used in both tooltip and popover variants
 */
function BadgeElement({
  badgeContent,
  displayLabel,
  isClickable,
}: {
  badgeContent: ReturnType<typeof getDiscountBadgeContent>;
  displayLabel: string;
  isClickable: boolean;
}) {
  return (
    <MDBox
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      gap={0.5}
      sx={{
        px: 1,
        py: 0.5,
        borderRadius: '4px',
        bgcolor: badgeContent.bgcolor,
        cursor: isClickable ? 'pointer' : 'help',
        minWidth: '110px',
        '&:hover': isClickable
          ? {
              filter: 'brightness(0.95)',
            }
          : undefined,
      }}
    >
      {badgeContent.icon}
      <MDTypography
        variant="caption"
        sx={{
          color: badgeContent.color,
          fontWeight: 500,
          fontSize: '0.75rem',
          whiteSpace: 'nowrap',
        }}
      >
        {displayLabel}
      </MDTypography>
    </MDBox>
  );
}

/**
 * Popover content for discount badges with discount info and navigation
 */
function DiscountPopoverContent({
  validation,
  customerId,
  customerName,
  productVariantId,
  sku,
  onCreateDiscount,
  onViewAttachment,
}: {
  validation: DiscountValidationResult;
  customerId?: string;
  customerName?: string;
  productVariantId?: string;
  sku?: string;
  onCreateDiscount?: () => void;
  onViewAttachment?: () => void;
}) {
  const navigate = useNavigate();
  const { status, poUnitPrice, discount, discrepancy, listPrice } = validation;

  const handleViewInDiscounts = () => {
    if (!discount?.sku) return;

    const searchParams: {
      sku?: string;
      customerId?: string;
      customerName?: string;
    } = {
      sku: discount.sku,
    };

    if (customerId && customerName) {
      searchParams.customerId = customerId;
      searchParams.customerName = customerName;
    }

    navigate({
      to: '/discounts',
      search: searchParams,
    });
  };

  const isApproved = status === DiscountStatus.APPROVED_DISCOUNT;
  const isUnapproved = status === DiscountStatus.UNAPPROVED_DISCOUNT;
  const isBelowListPrice = !discount && listPrice && isUnapproved;

  // Show create discount button when:
  // 1. Status is UNAPPROVED_DISCOUNT
  // 2. We have all required data to create a discount
  // Note: SKU is optional - we can use a placeholder if not available
  const canCreateDiscount =
    isUnapproved &&
    customerId &&
    customerName &&
    productVariantId &&
    poUnitPrice != null;

  return (
    <div className="space-y-3 p-1">
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          {isApproved ? (
            <CheckCircleIcon size={16} className="text-green-600" />
          ) : (
            <WarningIcon size={16} className="text-yellow-600" />
          )}
          <span className="font-medium text-sm">
            {isApproved
              ? 'Approved Discount'
              : isBelowListPrice
                ? 'Unapproved Price'
                : 'Requires Review'}
          </span>
        </div>

        {isBelowListPrice && (
          <div className="text-xs text-muted-foreground">
            PO price is below list price with no discount applied
          </div>
        )}

        <div className="text-sm space-y-1 text-muted-foreground">
          <div className="flex justify-between">
            <span>PO Price:</span>
            <span className="font-medium text-foreground">
              {formatCurrency(poUnitPrice)}
            </span>
          </div>
          {discount && (
            <div className="flex justify-between">
              <span>Discount Price:</span>
              <span className="font-medium text-foreground">
                {formatCurrency(discount.discountPrice)}
              </span>
            </div>
          )}
          {listPrice && !discount && (
            <div className="flex justify-between">
              <span>List Price:</span>
              <span className="font-medium text-foreground">
                {formatCurrency(listPrice.price)}
              </span>
            </div>
          )}
          {discrepancy && (
            <div className="flex justify-between text-amber-600">
              <span>Difference:</span>
              <span className="font-medium">
                {formatCurrency(discrepancy.difference)}
              </span>
            </div>
          )}
          {discount?.validUntil && (
            <div className="flex justify-between">
              <span>Valid Until:</span>
              <span>{new Date(discount.validUntil).toLocaleDateString()}</span>
            </div>
          )}
          {discount?.sku && (
            <div className="flex justify-between">
              <span>SKU:</span>
              <span className="font-mono text-foreground">{discount.sku}</span>
            </div>
          )}
        </div>
      </div>

      <div className="space-y-2">
        {discount?.attachment && onViewAttachment && (
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={onViewAttachment}
          >
            <PaperclipIcon size={14} className="mr-2" />
            View Attachment
          </Button>
        )}

        {discount?.sku && (
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={handleViewInDiscounts}
          >
            <ArrowSquareOutIcon size={14} className="mr-2" />
            View in Discounts
          </Button>
        )}

        {canCreateDiscount && onCreateDiscount && (
          <Button
            variant="default"
            size="sm"
            className="w-full"
            onClick={onCreateDiscount}
          >
            <PlusIcon size={14} className="mr-2" />
            Create Discount
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * Popover content for customer-specific discount verification
 */
function CustomerSpecificDiscountPopoverContent({
  validation,
  verification,
  customerName,
  onWaive,
  isWaiving,
}: {
  validation: DiscountValidationResult;
  verification?: CustomerSpecificDiscountVerification;
  customerName?: string;
  onWaive?: () => void;
  isWaiving?: boolean;
}) {
  const { discount, endCustomer } = validation;
  const verificationStatus = verification?.status;

  const isResolved =
    verificationStatus === 'VERIFIED' || verificationStatus === 'WAIVED';

  const getStatusContent = () => {
    switch (verificationStatus) {
      case 'VERIFIED':
        return {
          icon: <UserCheckIcon size={16} className="text-green-600" />,
          text: 'Verified',
          description: 'Discount auto-verified by the system',
        };
      case 'VERIFICATION_REQUESTED':
        return {
          icon: <ChatIcon size={16} className="text-amber-500" />,
          text: 'Evidence Requested',
          description: 'Waiting for customer to provide proof',
        };
      case 'WAIVED':
        return {
          icon: <ShieldCheckIcon size={16} className="text-blue-500" />,
          text: 'Waived',
          description: 'Verification requirement waived by analyst',
        };
      default:
        return {
          icon: <WarningIcon size={16} className="text-purple-600" />,
          text: 'Pending Verification',
          description: null, // Custom description with customer name below
        };
    }
  };

  const statusContent = getStatusContent();

  return (
    <div className="space-y-3 p-1">
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          {statusContent.icon}
          <span className="font-medium text-sm">{statusContent.text}</span>
        </div>

        {statusContent.description ? (
          <div className="text-xs text-muted-foreground">
            {statusContent.description}
          </div>
        ) : (
          <div className="text-xs text-muted-foreground">
            Verify {customerName || 'customer'} will sell to:
          </div>
        )}

        {endCustomer && !isResolved && (
          <div className="bg-purple-50 dark:bg-purple-950 border border-purple-200 dark:border-purple-800 rounded-md p-2 mt-1">
            <div className="text-xs text-purple-700 dark:text-purple-300 font-medium">
              End Customer
            </div>
            <div className="text-sm font-semibold text-purple-900 dark:text-purple-100">
              {endCustomer.name}
            </div>
          </div>
        )}

        <div className="text-sm space-y-1 text-muted-foreground mt-2">
          {discount && (
            <div className="flex justify-between">
              <span>Discounted Price:</span>
              <span className="font-medium text-foreground">
                {formatCurrency(discount.discountPrice)}
              </span>
            </div>
          )}
          {verification?.verifiedAt && (
            <div className="flex justify-between text-xs">
              <span>Updated:</span>
              <span>
                {new Date(verification.verifiedAt).toLocaleDateString()}
              </span>
            </div>
          )}
          {verification?.notes && (
            <div className="mt-2 text-xs italic border-t pt-2">
              Note: {verification.notes}
            </div>
          )}
        </div>
      </div>

      {!isResolved && onWaive && (
        <div className="space-y-2 border-t pt-3">
          <div className="text-xs text-muted-foreground mb-2">
            Manually approve without verification:
          </div>
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={onWaive}
            disabled={isWaiving}
          >
            <ShieldCheckIcon size={14} className="mr-2" />
            {isWaiving ? 'Waiving...' : 'Waive Verification'}
          </Button>
        </div>
      )}
    </div>
  );
}

const NO_DISCOUNT_BADGE_CONTENT = {
  icon: <MinusIcon size={14} className="text-gray-500" />,
  label: 'No Discount',
  color: 'text.secondary',
  bgcolor: 'rgba(0, 0, 0, 0.08)',
};

const LOADING_BADGE_CONTENT = {
  icon: <CircleNotchIcon size={14} className="text-blue-500 animate-spin" />,
  label: 'Checking...',
  color: '#0288d1',
  bgcolor: 'rgba(2, 136, 209, 0.1)',
};

export function DiscountBadge({
  discountValidation,
  customerId,
  customerName,
  productVariantId,
  sku,
  isLoading,
  customerSpecificDiscountVerification,
  onWaive,
  isWaiving,
}: DiscountBadgeProps) {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isAttachmentViewerOpen, setIsAttachmentViewerOpen] = useState(false);

  // Show loading state while discount validation is in progress
  if (isLoading) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div>
            <BadgeElement
              badgeContent={LOADING_BADGE_CONTENT}
              displayLabel={LOADING_BADGE_CONTENT.label}
              isClickable={false}
            />
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <span className="text-xs">Checking discount and price data...</span>
        </TooltipContent>
      </Tooltip>
    );
  }

  // If no discount validation data, show neutral "No Discount" badge
  if (!discountValidation) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div>
            <BadgeElement
              badgeContent={NO_DISCOUNT_BADGE_CONTENT}
              displayLabel={NO_DISCOUNT_BADGE_CONTENT.label}
              isClickable={false}
            />
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <span className="text-xs">No discount validation available</span>
        </TooltipContent>
      </Tooltip>
    );
  }

  const badgeContent = getDiscountBadgeContent(
    discountValidation.status,
    customerSpecificDiscountVerification?.status,
  );
  const displayLabel = badgeContent.label;

  // For APPROVED_DISCOUNT and UNAPPROVED_DISCOUNT with a discount or list price info, use Popover
  const hasDiscount =
    discountValidation.discount &&
    (discountValidation.status === DiscountStatus.APPROVED_DISCOUNT ||
      discountValidation.status === DiscountStatus.UNAPPROVED_DISCOUNT);

  // Also use popover for UNAPPROVED_DISCOUNT with list price (below list price case)
  const hasPriceIssue =
    !discountValidation.discount &&
    discountValidation.listPrice &&
    discountValidation.status === DiscountStatus.UNAPPROVED_DISCOUNT;

  // Check if this is a customer-specific discount requiring verification
  const isCustomerSpecificDiscount =
    discountValidation.status ===
    DiscountStatus.CUSTOMER_SPECIFIC_DISCOUNT_PENDING;

  // Get SKU from validation data if not provided directly
  // Note: listPrice doesn't have a sku field, only discount has it
  const displaySku = sku || discountValidation.discount?.sku;

  // Get values for create discount modal
  const poUnitPrice = discountValidation.poUnitPrice;
  const listPrice = discountValidation.listPrice?.price ?? null;

  const handleCreateDiscount = () => {
    setIsCreateModalOpen(true);
  };

  const handleViewAttachment = () => {
    setIsAttachmentViewerOpen(true);
  };

  // Get attachment URL from discount if available
  const attachmentUrl = discountValidation.discount?.attachment;

  // Handle customer-specific discount with verification popover
  if (isCustomerSpecificDiscount) {
    return (
      <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
        <PopoverTrigger asChild>
          <div>
            <BadgeElement
              badgeContent={badgeContent}
              displayLabel={displayLabel}
              isClickable={true}
            />
          </div>
        </PopoverTrigger>
        <PopoverContent
          className="w-72"
          align="start"
          onMouseLeave={() => setIsPopoverOpen(false)}
        >
          <CustomerSpecificDiscountPopoverContent
            validation={discountValidation}
            verification={customerSpecificDiscountVerification}
            customerName={customerName}
            onWaive={() => {
              onWaive?.();
              setIsPopoverOpen(false);
            }}
            isWaiving={isWaiving}
          />
        </PopoverContent>
      </Popover>
    );
  }

  if (hasDiscount || hasPriceIssue) {
    return (
      <>
        <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
          <PopoverTrigger asChild>
            <div>
              <BadgeElement
                badgeContent={badgeContent}
                displayLabel={displayLabel}
                isClickable={true}
              />
            </div>
          </PopoverTrigger>
          <PopoverContent
            className="w-64"
            align="start"
            onMouseLeave={() => setIsPopoverOpen(false)}
          >
            <DiscountPopoverContent
              validation={discountValidation}
              customerId={customerId}
              customerName={customerName}
              productVariantId={productVariantId}
              sku={displaySku}
              onCreateDiscount={handleCreateDiscount}
              onViewAttachment={
                attachmentUrl ? handleViewAttachment : undefined
              }
            />
          </PopoverContent>
        </Popover>

        {/* Create Discount Modal */}
        {customerId && customerName && productVariantId && (
          <CreateDiscountModal
            open={isCreateModalOpen}
            onOpenChange={setIsCreateModalOpen}
            customerId={customerId}
            customerName={customerName}
            productVariantId={productVariantId}
            sku={displaySku || 'Unknown SKU'}
            poUnitPrice={poUnitPrice}
            listPrice={listPrice}
          />
        )}

        {/* Attachment Viewer Modal */}
        {attachmentUrl && (
          <AttachmentViewerModal
            open={isAttachmentViewerOpen}
            onOpenChange={setIsAttachmentViewerOpen}
            attachmentUrl={attachmentUrl}
            title="Discount Attachment"
          />
        )}
      </>
    );
  }

  // For other statuses (STANDARD_PRICE, NO_LIST_PRICE), use Tooltip
  const tooltipContent = getTooltipContent(discountValidation);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div>
          <BadgeElement
            badgeContent={badgeContent}
            displayLabel={displayLabel}
            isClickable={false}
          />
        </div>
      </TooltipTrigger>
      <TooltipContent>{tooltipContent}</TooltipContent>
    </Tooltip>
  );
}

export default DiscountBadge;
